import React,{useState} from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiExternalLink,FiTrendingUp,FiDollarSign,FiUsers,FiArrowRight,FiFileText}=FiIcons;

function CaseStudies() {
  const [hoveredCard,setHoveredCard]=useState(null);
  
  const caseStudies=[
    {
      id: 1,
      title: "319 LOST Leads Turned Into $49,000 Of Sales",
      subtitle: "Give Us The Leads You've Given Up On & We'll Use AI To Turn Them Into Sales",
      description: "In 2025,it is possible to turn the leads you've given up on into cash in the bank WITHOUT hiring extra sales people,paying appointment setting agencies,or dialing the leads yourself.",
      highlights: [
        {icon: FiUsers,label: "319 Lost Leads Reactivated"},
        {icon: FiDollarSign,label: "$49,000 Generated"},
        {icon: FiTrendingUp,label: "Zero Extra Staff"}
      ],
      companyType: "Debt Consolidation Company",
      link: "https://docs.google.com/document/d/1uCuokSWv5briMQbAzaj6sV-zh7ccFboFZ-0GdCSbjok/edit?usp=sharing",
      color: "from-blue-500 to-cyan-500"
    },
    {
      id: 2,
      title: "Can Your Best Salesperson Produce $600k+ Profit in 90 Days?",
      subtitle: "Have Your Cake AND Eat It Too",
      description: "The below stats are for a Debt Consolidation company that leveraged AI to achieve extraordinary results in just 90 days.",
      highlights: [
        {icon: FiDollarSign,label: "$600k+ Profit"},
        {icon: FiTrendingUp,label: "90 Days Timeframe"},
        {icon: FiUsers,label: "Industry Leading Results"}
      ],
      companyType: "Debt Consolidation Company",
      link: "https://docs.google.com/document/d/1kEI5dQZO-WZi3U6qvGY5Q3fmR0NLIdoBovRXGoiqrmk/edit?usp=sharing",
      color: "from-green-500 to-emerald-500"
    }
  ];

  return (
    <section className="pt-32 pb-24 bg-gradient-to-br from-gray-50 via-white to-orange-50 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          initial={{y: 30,opacity: 0}} 
          animate={{y: 0,opacity: 1}} 
          transition={{duration: 0.8}} 
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-orange-100 text-orange-600 px-6 py-2 rounded-full font-semibold mb-6">
            <SafeIcon icon={FiFileText} className="w-4 h-4" />
            <span>Success Stories</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Case Studies
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real results from real businesses using AI to transform their sales process
          </p>
        </motion.div>

        {/* Case Studies Grid */}
        <div className="space-y-12" id="case-study-container">
          {caseStudies.map((study,index)=> (
            <motion.div 
              key={study.id} 
              initial={{y: 50,opacity: 0}} 
              animate={{y: 0,opacity: 1}} 
              transition={{duration: 0.8,delay: index * 0.2}} 
              className="group"
              id={study.id === 1 ? 'case-study-1' : 'case-study-2'}
            >
              <motion.div 
                whileHover={{y: -10}} 
                onHoverStart={()=> setHoveredCard(study.id)} 
                onHoverEnd={()=> setHoveredCard(null)} 
                className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden"
              >
                {/* Header Gradient Bar */}
                <div className={`h-2 bg-gradient-to-r ${study.color}`}></div>
                
                <div className="p-8 md:p-12">
                  <div className="grid lg:grid-cols-2 gap-8 items-center">
                    {/* Left Content */}
                    <div className="space-y-6">
                      <div>
                        <div className="inline-flex items-center space-x-2 bg-orange-100 text-orange-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                          <SafeIcon icon={FiFileText} className="w-3 h-3" />
                          <span>{study.companyType}</span>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                          {study.title}
                        </h2>
                        <h3 className="text-xl text-orange-500 font-semibold mb-6">
                          {study.subtitle}
                        </h3>
                      </div>
                      
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {study.description}
                      </p>
                      
                      {/* Highlights */}
                      <div className="space-y-3">
                        {study.highlights.map((highlight,idx)=> (
                          <div key={idx} className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${study.color} flex items-center justify-center flex-shrink-0`}>
                              <SafeIcon icon={highlight.icon} className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-gray-700 font-medium">{highlight.label}</span>
                          </div>
                        ))}
                      </div>
                      
                      {/* CTA Button */}
                      <motion.a 
                        href={study.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        whileHover={{scale: 1.05}} 
                        whileTap={{scale: 0.95}} 
                        className={`inline-flex items-center space-x-2 bg-gradient-to-r ${study.color} text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 group`}
                      >
                        <span>Read Full Case Study</span>
                        <SafeIcon icon={FiExternalLink} className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                      </motion.a>
                    </div>
                    
                    {/* Right Visual */}
                    <motion.div 
                      initial={{scale: 0.9,opacity: 0}} 
                      animate={{scale: 1,opacity: 1}} 
                      transition={{duration: 0.8,delay: index * 0.2 + 0.3}} 
                      className="relative"
                    >
                      <div className={`bg-gradient-to-br ${study.color} rounded-2xl p-8 text-white relative overflow-hidden`}>
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute inset-0" style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                          }}></div>
                        </div>
                        
                        <div className="relative z-10">
                          <div className="text-4xl md:text-5xl font-bold mb-4">
                            {study.id===1 ? '$49,000' : '$600k+'}
                          </div>
                          <div className="text-xl mb-6 opacity-90">
                            {study.id===1 ? 'From Lost Leads' : 'Profit in 90 Days'}
                          </div>
                          <div className="flex items-center space-x-2 text-white/80">
                            <SafeIcon icon={FiTrendingUp} className="w-5 h-5" />
                            <span>Proven Results</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div 
          initial={{y: 30,opacity: 0}} 
          animate={{y: 0,opacity: 1}} 
          transition={{duration: 0.8,delay: 0.6}} 
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Be Our Next Success Story?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Join the businesses already transforming their sales process with AI automation
            </p>
            <Link to="/contact">
              <motion.button 
                whileHover={{scale: 1.05,y: -2}} 
                whileTap={{scale: 0.95}} 
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-xl font-bold text-lg flex items-center space-x-2 mx-auto shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <span>Start Your Journey</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </motion.button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default CaseStudies;